import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef } from 'react';
import { useFormik } from 'formik';
import { Button, Textarea } from '~/ui/index';
import UploadPhotoItem from '~/components/UploadPhoto/UploadPhotoItem/UploadPhotoItem';
import { type UploadPhotosValue } from '~/components/UploadPhoto/UploadPhoto';
import styles from './RequestForm.module.scss';
import { VideoPlayer } from '~/components/Shared/VideoPlayer/VideoPlayer';
import { useTranslation } from 'react-i18next';
import { translateCategoryName } from '~/utils/translateOptions';
import { getTimeDates } from '~/utils/getTimeDates';
import cn from 'classnames';
import UploadIcon from '~/assets/svg/newSvg/upload-icon.svg?react';

export interface Photo {
  id: number;
  full_photo_url: string;
}

interface Video {
  id: number;
  full_video_url: string;
  full_preview_video_url: string;
}

export interface RequestFormItem {
  id: number;
  category_name: string;
  object_name: string;
  description: string;
  ticket_status: number;
  user_name: string;
  grade_name: string;
  created_at: string;
  user_uniqueId: string;
  ticket_photo: Photo[];
  ticket_video: Video[];
  ticket_review_photo: Photo[];
  ticket_review_video: Video[];
  business_trip: {
    accommodation: { name: string; section: string };
    room: { name: string };
  };
  working_period: string | null;
  user_id: number;
  review: string | null;
}

interface RequestFormProps {
  request: RequestFormItem;
  solution: string;
  setSolution: (solution: string) => void;
  updateTicketSolution: (params: {
    id: number;
    review: string;
    reviewImages: File[];
    reviewVideos: File[];
  }) => void;
  onReviewFilesChange?: (images: File[], videos: File[]) => void;
}

interface InfoFieldProps {
  label: string;
  value: string;
  muted?: boolean;
  children?: React.ReactNode;
}

const InfoField = ({ label, value, muted, children }: InfoFieldProps) => (
  <div className={styles.infoField}>
    <div className={styles.infoLabelRow}>
      <span className={styles.infoLabel}>{label}</span>
      {children}
    </div>
    <div className={cn(styles.infoValue, { [styles.infoValueMuted]: muted })}>
      {value}
    </div>
  </div>
);

export const RequestForm = ({
  request,
  setSolution,
  updateTicketSolution,
  onReviewFilesChange,
}: RequestFormProps) => {
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const { t } = useTranslation();
  const videoInputRef = useRef<HTMLInputElement>(null);

  const photos = request?.ticket_photo;
  const name = request?.business_trip?.accommodation?.name || '';
  const room = request?.business_trip?.room?.name || '';
  const section = request?.business_trip?.accommodation?.section || '';
  const review = request?.review || '';

  const handleUpdate = () => {
    updateTicketSolution({
      id: request.id,
      review: formik.values.solution,
      reviewImages: formik.values.uploadReviewPhotos.files,
      reviewVideos: formik.values.reviewVideoFiles,
    });
  };

  const finallyInfo = [name, section, room].filter(Boolean).join(' / ');

  const formik = useFormik({
    initialValues: {
      category: '',
      object_name: '',
      comments: '',
      status: '',
      user_name: '',
      grade: '',
      date: '',
      uniqueID: '',
      working_period: '',
      solution: '',
      uploadReviewPhotos: { photos: [], files: [] } as UploadPhotosValue,
      reviewVideoFiles: [] as File[],
    },
    onSubmit: () => {
      formik.resetForm();
    },
  });

  const videoFileUrls = useMemo(
    () => formik.values.reviewVideoFiles.map(file => URL.createObjectURL(file)),
    [formik.values.reviewVideoFiles]
  );

  useEffect(() => {
    return () => {
      videoFileUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [videoFileUrls]);

  useEffect(() => {
    if (!request) return;
    formik.setValues(prev => ({
      ...prev,
      category: translateCategoryName(request.category_name, t),
      object_name: request.object_name,
      comments: request.description,
      status: request.ticket_status?.toString() ?? '',
      user_name: request.user_name,
      grade: request.grade_name,
      date: getTimeDates(request.created_at),
      uniqueID: request.user_uniqueId,
      working_period: request.working_period || '',
      solution: request.review || '',
    }));
  }, [request]);

  const hasChanges =
    review !== formik.values.solution ||
    formik.values.uploadReviewPhotos.files.length > 0 ||
    formik.values.reviewVideoFiles.length > 0;

  const empty = (val: string) => !val || val.trim() === '';

  const hasReviewMedia =
    (request?.ticket_review_photo?.length ?? 0) > 0 ||
    formik.values.uploadReviewPhotos.files.length > 0 ||
    (request?.ticket_review_video?.length ?? 0) > 0 ||
    formik.values.reviewVideoFiles.length > 0;

  return (
    <div className={styles.formWrapper}>
      <form autoComplete="off" onSubmit={formik.handleSubmit}>
        <div className={styles.formLayout}>

          <div className={styles.fieldsGrid}>
            <InfoField
              label={t('category')}
              value={formik.values.category || t('not_specified')}
              muted={empty(formik.values.category)}
            />
            <InfoField
              label={t('shift')}
              value={formik.values.working_period || t('not_specified')}
              muted={empty(formik.values.working_period)}
            />
          </div>

          <InfoField
            label={t('object_place')}
            value={formik.values.object_name || t('not_specified')}
            muted={empty(formik.values.object_name)}
          />

          <InfoField
            label={`${t('application_created_on')}:`}
            value={finallyInfo || t('not_specified')}
            muted={empty(finallyInfo)}
          />

          <InfoField
            label={t('unique_user_ID')}
            value={formik.values.uniqueID || t('not_specified')}
            muted={empty(formik.values.uniqueID)}
          />

          <div className={styles.fieldsGrid3}>
            <InfoField
              label={t('employee')}
              value={formik.values.user_name || t('not_specified')}
              muted={empty(formik.values.user_name)}
            >
              {request?.user_id && (
                <a href={`/user/${request.user_id}`} className={styles.contactLink}>
                  {t('to_contact')}
                </a>
              )}
            </InfoField>
            <InfoField
              label={t('access_group')}
              value={formik.values.grade || t('not_specified')}
              muted={empty(formik.values.grade)}
            />
            <InfoField
              label={t('date_and_time')}
              value={formik.values.date || t('not_specified')}
              muted={empty(formik.values.date)}
            />
          </div>

          <div className={styles.fieldBlock}>
            <span className={styles.blockLabel}>{t('description')}</span>
            <Textarea
              showCounter
              rows={5}
              label=""
              placeholder={t('leave_comment')}
              maxLength={1000}
              value={formik.values.comments}
              onChange={formik.handleChange}
              name="comments"
              star={false}
              disabled
              readOnly
            />
            {(photos?.length > 0 || request?.ticket_video?.length > 0) && (
              <div className={styles.mediaGrid}>
                {photos?.map(({ id, full_photo_url }) => (
                  <UploadPhotoItem key={id} url={full_photo_url} deletePhoto={false} />
                ))}
                {request.ticket_video.map(({ id, full_video_url, full_preview_video_url }) => (
                  <VideoPlayer key={id} url={full_video_url} poster={full_preview_video_url} />
                ))}
              </div>
            )}
          </div>

          <div className={styles.fieldBlock}>
            <span className={styles.blockLabel}>{t('solution')}</span>
            <Textarea
              label=""
              name="solution"
              placeholder={t('solution_placeholder')}
              value={formik?.values?.solution}
              onChange={e => {
                formik.setFieldValue('solution', e.target.value);
                setSolution(e.target.value);
              }}
              showCounter
              rows={7}
              maxLength={1000}
              star={false}
            />
          </div>

          <div className={styles.fieldBlock}>
            <span className={styles.blockLabel}>{t('photos_and_videos_for_solution')}</span>
            {hasReviewMedia && (
              <div className={styles.reviewMediaGrid}>
                {request?.ticket_review_photo?.map(({ id, full_photo_url }) => (
                  <UploadPhotoItem key={id} url={full_photo_url} deletePhoto={false} />
                ))}
                {formik.values.uploadReviewPhotos.files.map((file, index) => (
                  <UploadPhotoItem
                    key={file.name + index}
                    url={URL.createObjectURL(file)}
                    deletePhoto
                    onClick={() => {
                      const newFiles = [...formik.values.uploadReviewPhotos.files];
                      newFiles.splice(index, 1);
                      formik.setFieldValue('uploadReviewPhotos', { photos: [], files: newFiles });
                      onReviewFilesChange?.(newFiles, formik.values.reviewVideoFiles);
                    }}
                  />
                ))}
                {request?.ticket_review_video?.map(({ id, full_video_url, full_preview_video_url }) => (
                  <VideoPlayer key={id} url={full_video_url} poster={full_preview_video_url} />
                ))}
                {formik.values.reviewVideoFiles.map((file, index) => (
                  <div key={file.name + index} className={styles.reviewVideoItem}>
                    <video controls className={styles.reviewVideoPreview} src={videoFileUrls[index]} />
                    <button
                      type="button"
                      className={styles.reviewVideoDelete}
                      onClick={() => {
                        const newFiles = [...formik.values.reviewVideoFiles];
                        newFiles.splice(index, 1);
                        formik.setFieldValue('reviewVideoFiles', newFiles);
                        onReviewFilesChange?.(formik.values.uploadReviewPhotos.files, newFiles);
                      }}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label className={styles.uploadArea}>
              <div className={styles.uploadAreaIcon}>
                <UploadIcon />
              </div>
              <div className={styles.uploadAreaText}>
                <span className={styles.uploadAreaTitle}>{t('upload_photo_or_video')}</span>
                <span className={styles.uploadAreaHint}>{t('upload_photo_video_hint')}</span>
              </div>
              <input
                ref={videoInputRef}
                type="file"
                accept="image/png,image/jpg,image/jpeg,video/*"
                multiple
                className={styles.uploadInput}
                onChange={e => {
                  const newFiles = e.target.files;
                  if (!newFiles) return;
                  const imageFiles: File[] = [];
                  const videoFiles: File[] = [];
                  Array.from(newFiles).forEach(file => {
                    if (file.type.startsWith('image/')) imageFiles.push(file);
                    else if (file.type.startsWith('video/')) videoFiles.push(file);
                  });
                  if (imageFiles.length > 0) {
                    formik.setFieldValue('uploadReviewPhotos', {
                      photos: [],
                      files: [...formik.values.uploadReviewPhotos.files, ...imageFiles],
                    });
                  }
                  if (videoFiles.length > 0) {
                    formik.setFieldValue('reviewVideoFiles', [
                      ...formik.values.reviewVideoFiles,
                      ...videoFiles,
                    ]);
                  }
                  onReviewFilesChange?.(
                    imageFiles.length > 0
                      ? [...formik.values.uploadReviewPhotos.files, ...imageFiles]
                      : formik.values.uploadReviewPhotos.files,
                    videoFiles.length > 0
                      ? [...formik.values.reviewVideoFiles, ...videoFiles]
                      : formik.values.reviewVideoFiles
                  );
                  e.target.value = '';
                }}
              />
            </label>
          </div>

          <div className={styles.formAction}>
            {hasChanges ? (
              <Button
                type="button"
                text={t('save_n_exit')}
                className={styles.btn}
                onClick={handleUpdate}
                disabled={false}
              />
            ) : (
              <Button
                type="submit"
                text={t('back_to_list')}
                className={styles.btn}
                onClick={goBack}
                disabled={false}
              />
            )}
          </div>

        </div>
      </form>
    </div>
  );
};
